import type { QueryClient, QueryFilters } from "@tanstack/react-query";

export type QueryDataSnapshot<T> = {
  queryKey: readonly unknown[];
  data: T;
};

export const snapshotQueriesData = <T>(
  queryClient: QueryClient,
  filters: QueryFilters,
): QueryDataSnapshot<T>[] => {
  const entries = queryClient.getQueriesData<T>(filters);
  return entries.flatMap(([queryKey, data]) =>
    data === undefined ? [] : [{ queryKey, data }],
  );
};

export const restoreQueriesData = <T>(
  queryClient: QueryClient,
  snapshots: QueryDataSnapshot<T>[],
) => {
  for (const snapshot of snapshots) {
    queryClient.setQueryData(snapshot.queryKey, snapshot.data);
  }
};

interface ApplyOptimisticSetQueriesDataParams<T> {
  cancelFilters: QueryFilters;
  snapshotFilters: QueryFilters;
  updateFilters: QueryFilters;
  updater: (oldData: T | undefined) => T | undefined;
}

export const applyOptimisticSetQueriesData = async <T>(
  queryClient: QueryClient,
  params: ApplyOptimisticSetQueriesDataParams<T>,
): Promise<QueryDataSnapshot<T>[]> => {
  const { cancelFilters, snapshotFilters, updateFilters, updater } = params;

  await queryClient.cancelQueries(cancelFilters);
  const snapshots = snapshotQueriesData<T>(queryClient, snapshotFilters);
  queryClient.setQueriesData<T>(updateFilters, updater);
  return snapshots;
};
